import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, Video, ExternalLink, RefreshCw, Settings, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import GoogleCalendarService from '../../services/googleCalendarService';
import { supabase, TABLES } from '../../config/supabase';
import logger from '../../utils/logger';

const CalendarView = () => {
  const { meetings, fetchMeetings, userTimezone, formatDateInUserTimezone, formatTimeInUserTimezone } = useData();
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState(null);
  const [showAllEventsModal, setShowAllEventsModal] = useState(false);

  // Check if Google Calendar integration is enabled
  useEffect(() => {
    const isEnabled = GoogleCalendarService.isEnabled();
    setSyncEnabled(isEnabled);
    if (isEnabled) {
      loadCalendarEvents();
    }
  }, []);
  
  // CRITICAL FIX: Load meetings when component mounts
  useEffect(() => {
    fetchMeetings();
  }, []);

  const loadCalendarEvents = async () => {
    if (!syncEnabled) return;
    
    setLoading(true);
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const events = await GoogleCalendarService.fetchEvents(
        startOfMonth.toISOString(),
        endOfMonth.toISOString()
      );
      
      setCalendarEvents(events || []);
    } catch (error) {
      logger.error('Failed to load calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCalendar = async () => {
    await Promise.all([
      loadCalendarEvents(),
      fetchMeetings()
    ]);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    
    // CRITICAL FIX: Proper date comparison for Helsinki timezone
    // Simply format the calendar date as YYYY-MM-DD for direct comparison
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const targetDateStr = `${year}-${month}-${day}`;
    
    const crmMeetings = meetings.filter(meeting => {
      // meeting_date is stored as YYYY-MM-DD format in Helsinki timezone
      const meetingDateStr = meeting.meeting_date;
      
      logger.log('📅 FIXED Date comparison:', {
        calendarDate: targetDateStr,
        meetingDate: meetingDateStr,
        match: meetingDateStr === targetDateStr,
        meetingTitle: meeting.title
      });
      
      return meetingDateStr === targetDateStr;
    });
    
    const googleEvents = calendarEvents.filter(event => {
      // Convert Google Calendar event to Helsinki timezone for comparison
      const eventDateTime = new Date(event.start?.dateTime || event.start?.date);
      const eventInHelsinki = new Date(eventDateTime.toLocaleString('en-US', { timeZone: 'Europe/Helsinki' }));
      const eventDateStr = eventInHelsinki.toISOString().split('T')[0];
      
      return eventDateStr === targetDateStr;
    });
    
    return [...crmMeetings, ...googleEvents];
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Update meeting status from calendar using booking_status table
  const updateMeetingStatus = async (meetingId, newStatus) => {
    setLoading(true);
    try {
      logger.log('Calendar: Attempting to update booking status:', { meetingId, newStatus });
      
      // Update or insert booking status in the separate table
      const { error } = await supabase
        .from('booking_status')
        .upsert({ 
          meeting_id: meetingId,
          status: newStatus,
          status_updated_by: 'user',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'meeting_id'
        });

      if (error) {
        logger.error('Calendar: Error updating booking status:', error);
        alert('Failed to update meeting status: ' + error.message);
        return;
      }

      logger.log('Calendar: Booking status updated successfully');
      // Refresh meetings to show updated status
      await fetchMeetings();
      alert(`Meeting marked as ${newStatus}`);
    } catch (error) {
      logger.error('Calendar: Error updating booking status:', error);
      alert('Failed to update meeting: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Get status badge for display
  const getStatusBadge = (status) => {
    const currentStatus = status || 'pending';
    switch (currentStatus) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const EventCard = ({ event, isCrmEvent = false }) => {
    const startTime = isCrmEvent 
      ? event.meeting_time // Just use the time directly for CRM events
      : event.start?.dateTime || event.start?.date;
    
    const endTime = isCrmEvent 
      ? null
      : event.end?.dateTime || event.end?.date;

    return (
      <div 
        className={`p-2 rounded-md border-l-2 cursor-pointer transition-colors hover:shadow-sm ${
          isCrmEvent 
            ? 'bg-blue-50 border-l-blue-500 hover:bg-blue-100'
            : 'bg-green-50 border-l-green-500 hover:bg-green-100'
        }`}
        onClick={() => setSelectedEvent({ ...event, isCrmEvent })}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-xs text-gray-900 truncate">
              {isCrmEvent ? event.title : event.summary}
            </h4>
            <div className="flex items-center text-xs text-gray-600 mt-1">
              <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">
                {isCrmEvent ? (
                  (() => {
                    // CRITICAL FIX: Display Helsinki time directly without conversion
                    const [hours, minutes] = event.meeting_time.split(':').map(Number);
                    const helsinkiDate = new Date();
                    helsinkiDate.setHours(hours, minutes, 0, 0);
                    return helsinkiDate.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });
                  })()
                ) : (
                  `${formatTime(startTime)}${endTime ? ` - ${formatTime(endTime)}` : ''}`
                )}
              </span>
            </div>
            {(event.location || event.meeting_type) && (
              <div className="flex items-center text-xs text-gray-500 mt-1">
                <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{event.location || event.meeting_type}</span>
              </div>
            )}
          </div>
          <div className="ml-2 flex-shrink-0">
            <div className="flex flex-col space-y-1">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                isCrmEvent 
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {isCrmEvent ? 'CRM' : 'GCal'}
              </span>
              {/* Status indicator for CRM events */}
              {isCrmEvent && (
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                  (event.status || 'pending') === 'completed' 
                    ? 'bg-green-100 text-green-700'
                    : (event.status || 'pending') === 'cancelled'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {(event.status || 'pending') === 'completed' && <CheckCircle className="w-2 h-2 mr-1" />}
                  {(event.status || 'pending') === 'cancelled' && <XCircle className="w-2 h-2 mr-1" />}
                  {(event.status || 'pending') === 'pending' && <AlertCircle className="w-2 h-2 mr-1" />}
                  {(event.status || 'pending').charAt(0).toUpperCase() + (event.status || 'pending').slice(1)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AllEventsModal = ({ dateEvents, onClose, onEventClick }) => {
    if (!dateEvents) return null;

    const { date, events } = dateEvents;
    const dateString = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                All Events
              </h3>
              <p className="text-sm text-gray-500 mt-1">{dateString}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
            >
              ×
            </button>
          </div>
          
          <div className="overflow-y-auto max-h-[60vh] p-4 space-y-2">
            {events.map((event, index) => {
              const isCrmEvent = event.hasOwnProperty('meeting_date');
              return (
                <div 
                  key={`${isCrmEvent ? 'crm' : 'google'}-${event.id}-${index}`}
                  onClick={() => onEventClick({ ...event, isCrmEvent })}
                  className={`p-3 rounded-md border-l-2 cursor-pointer transition-colors hover:shadow-sm ${
                    isCrmEvent 
                      ? 'bg-blue-50 border-l-blue-500 hover:bg-blue-100'
                      : 'bg-green-50 border-l-green-500 hover:bg-green-100'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-gray-900 truncate">
                        {isCrmEvent ? event.title : event.summary}
                      </h4>
                      <div className="flex items-center text-xs text-gray-600 mt-1">
                        <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">
                          {isCrmEvent ? (
                            (() => {
                              // CRITICAL FIX: Display Helsinki time directly without conversion
                              const [hours, minutes] = event.meeting_time.split(':').map(Number);
                              const helsinkiDate = new Date();
                              helsinkiDate.setHours(hours, minutes, 0, 0);
                              return helsinkiDate.toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              });
                            })()
                          ) : (
                            formatTime(event.start?.dateTime || event.start?.date)
                          )}
                          {!isCrmEvent && event.end && (
                            ` - ${formatTime(event.end?.dateTime || event.end?.date)}`
                          )}
                        </span>
                      </div>
                      {(event.location || event.meeting_type) && (
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{event.location || event.meeting_type}</span>
                        </div>
                      )}
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        isCrmEvent 
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {isCrmEvent ? 'CRM' : 'GCal'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="p-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const EventModal = ({ event, onClose }) => {
    if (!event) return null;

    const { isCrmEvent } = event;
    const startTime = isCrmEvent 
      ? event.meeting_date // Use meeting_date for CRM events
      : event.start?.dateTime || event.start?.date;
    
    const endTime = isCrmEvent 
      ? null
      : event.end?.dateTime || event.end?.date;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {isCrmEvent ? event.title : event.summary}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="w-4 h-4 mr-2" />
              <span>
                {isCrmEvent ? (
                  (() => {
                    // FINAL FIX: Display stored Helsinki date and time directly without conversion
                    const [year, month, day] = event.meeting_date.split('-').map(Number);
                    const [hours, minutes] = event.meeting_time.split(':').map(Number);
                    
                    // Create date object for formatting (no timezone conversion needed)
                    const meetingDate = new Date(year, month - 1, day);
                    const dateStr = meetingDate.toLocaleDateString('en-US', { 
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                    
                    // Format time directly from stored Helsinki time
                    const hour12 = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    const timeStr = `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
                    
                    return `${dateStr} at ${timeStr} (Helsinki)`;
                  })()
                ) : (
                  `${formatDate(startTime)} at ${formatTime(startTime)}${endTime ? ` - ${formatTime(endTime)}` : ''}`
                )}
              </span>
            </div>
            
            {(event.location || event.meeting_type) && (
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="w-4 h-4 mr-2" />
                <span>{event.location || event.meeting_type}</span>
              </div>
            )}
            
            {event.attendees && event.attendees.length > 0 && (
              <div className="flex items-center text-sm text-gray-600">
                <Users className="w-4 h-4 mr-2" />
                <span>{event.attendees.length} attendees</span>
              </div>
            )}
            
            {(event.video_link || event.hangoutLink) && (
              <div className="flex items-center text-sm text-gray-600">
                <Video className="w-4 h-4 mr-2" />
                <a 
                  href={event.video_link || event.hangoutLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center"
                >
                  Join Video Call
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </div>
            )}
            
            {(event.description || event.notes) && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                <p className="text-sm text-gray-600">
                  {event.description || event.notes}
                </p>
              </div>
            )}
            
            {/* Status display for CRM events */}
            {isCrmEvent && (
              <div className="mt-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  {getStatusBadge(event.status)}
                </div>
              </div>
            )}

            {/* Contact information for CRM events */}
            {isCrmEvent && event.contact_name && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    <span>{event.contact_name}</span>
                  </div>
                  {event.contact_email && (
                    <div className="flex items-center">
                      <span className="w-4 h-4 mr-2">@</span>
                      <span>{event.contact_email}</span>
                    </div>
                  )}
                  {event.contact_phone && (
                    <div className="flex items-center">
                      <span className="w-4 h-4 mr-2">📞</span>
                      <span>{event.contact_phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {event.google_calendar_link && (
              <div className="mt-4">
                <a 
                  href={event.google_calendar_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  View in Google Calendar
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </div>
            )}
          </div>
          
          <div className="mt-6 flex justify-between">
            {/* Status update buttons for CRM events */}
            {isCrmEvent && (event.status || 'pending') === 'pending' && (
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    logger.log('Calendar: Marking meeting as completed:', event.id);
                    updateMeetingStatus(event.id, 'completed');
                    onClose();
                  }}
                  className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  disabled={loading}
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Mark Complete</span>
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    logger.log('Calendar: Marking meeting as cancelled:', event.id);
                    updateMeetingStatus(event.id, 'cancelled');
                    onClose();
                  }}
                  className="flex items-center space-x-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  disabled={loading}
                >
                  <XCircle className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
              </div>
            )}
            
            {/* Show status for completed/cancelled meetings */}
            {isCrmEvent && (event.status === 'completed' || event.status === 'cancelled') && (
              <div className="text-sm text-gray-600">
                Status: {event.status === 'completed' ? '✅ Completed' : '❌ Cancelled'}
              </div>
            )}
            
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors ml-auto"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!syncEnabled) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Google Calendar Integration Not Enabled
          </h3>
          <p className="text-gray-600 mb-4">
            To view your Google Calendar events, please enable the integration in your environment settings.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left max-w-md mx-auto">
            <h4 className="font-medium text-yellow-800 mb-2">Required Environment Variables:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• REACT_APP_GOOGLE_CALENDAR_ENABLED=true</li>
              <li>• REACT_APP_N8N_WEBHOOK_URL</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      {/* Compact Professional Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Calendar</h2>
                <p className="text-gray-500 text-xs">Manage your schedule</p>
              </div>
            </div>
            
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-white rounded-md transition-colors"
              >
                ←
              </button>
              <h3 className="text-sm font-medium text-gray-900 min-w-[160px] text-center px-3">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-white rounded-md transition-colors"
              >
                →
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={refreshCalendar}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Compact Professional Calendar Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Clean Days of week header */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
            <div key={day} className={`p-2 text-center font-medium text-gray-700 border-r border-gray-200 last:border-r-0 ${
              index === 0 || index === 6 ? 'bg-gray-100' : ''
            }`}>
              <div className="text-xs uppercase tracking-wide">{day}</div>
            </div>
          ))}
        </div>
        
        {/* Compact Calendar days */}
        <div className="grid grid-cols-7">
          {getDaysInMonth().map((date, index) => {
            const events = date ? getEventsForDate(date) : [];
            const isToday = date && date.toDateString() === new Date().toDateString();
            const isWeekend = date && (date.getDay() === 0 || date.getDay() === 6);
            
            return (
              <div 
                key={index} 
                className={`min-h-[100px] p-2 border-r border-b border-gray-200 last:border-r-0 transition-colors hover:bg-gray-50 ${
                  date ? 'bg-white cursor-pointer' : 'bg-gray-25'
                } ${
                  isToday ? 'bg-blue-50 border-blue-200' : ''
                } ${
                  isWeekend && date ? 'bg-gray-25' : ''
                }`}
              >
                {date && (
                  <>
                    <div className={`text-sm font-medium mb-2 ${
                      isToday 
                        ? 'text-blue-600 bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center text-xs' 
                        : 'text-gray-900'
                    }`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {events.slice(0, 2).map((event, eventIndex) => {
                        const isCrmEvent = event.hasOwnProperty('meeting_date');
                        return (
                          <div key={`${isCrmEvent ? 'crm' : 'google'}-${event.id}-${eventIndex}`}>
                            <EventCard 
                              event={event}
                              isCrmEvent={isCrmEvent}
                            />
                          </div>
                        );
                      })}
                      {events.length > 2 && (
                        <button 
                          onClick={() => {
                            setSelectedDateEvents({ date, events });
                            setShowAllEventsModal(true);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 text-center py-1 px-2 w-full rounded transition-colors"
                        >
                          +{events.length - 2} more
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Compact Legend */}
      <div className="mt-3 flex items-center justify-center space-x-4">
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
          <span className="text-xs text-gray-600">CRM Meetings</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
          <span className="text-xs text-gray-600">Google Calendar</span>
        </div>
      </div>

      {/* Event Modal */}
      {selectedEvent && (
        <EventModal 
          event={selectedEvent} 
          onClose={() => setSelectedEvent(null)} 
        />
      )}

      {/* All Events Modal */}
      {showAllEventsModal && selectedDateEvents && (
        <AllEventsModal 
          dateEvents={selectedDateEvents}
          onClose={() => {
            setShowAllEventsModal(false);
            setSelectedDateEvents(null);
          }}
          onEventClick={(event) => {
            setSelectedEvent(event);
            setShowAllEventsModal(false);
          }}
        />
      )}
    </div>
  );
};

export default CalendarView;